/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { AdPlaybackEvent } from '../../../event/AdPlaybackEvent';
import { ApplicationEvent } from '../../../event/ApplicationEvent';
import { PlaybackEvent } from '../../../event/PlaybackEvent';
import { RequestType } from '@quickplay/player-interface';
import { GodavariAdDetailType, GodavariPulseStage, GodavariPulsePlayerState } from '../model/GodavariPayload';

const APPLICATION_EVENT_ALIASES = new Map<string, ApplicationEvent>([
  ['AppBackgrounded', ApplicationEvent.APP_BACKGROUND],
  ['AppForegrounded', ApplicationEvent.APP_FOREGROUND],
]);

export function canonicalGodavariEventName(eventName: string): string {
  return APPLICATION_EVENT_ALIASES.get(eventName) ?? eventName;
}

type NetworkPulseStage = Omit<GodavariPulseStage, 'pulse_micro_steps'>;

const NETWORK_PULSE_EVENTS = new Map<RequestType, NetworkPulseStage>([
  [RequestType.MANIFEST, { pulse_start_steps: 'ManifestFetch', pulse_player_state: 'Manifest' }],
  [RequestType.SEGMENT, { pulse_start_steps: 'ChunkDownload', pulse_player_state: 'Chunk' }],
  [RequestType.LICENSE, { pulse_start_steps: 'LicenceUrlFetch', pulse_player_state: 'LicenceUrl' }],
]);

export function getNetworkPulseStage(requestType: unknown): NetworkPulseStage | undefined {
  if (
    requestType === RequestType.MANIFEST ||
    requestType === RequestType.SEGMENT ||
    requestType === RequestType.LICENSE
  ) {
    return NETWORK_PULSE_EVENTS.get(requestType);
  }
  return undefined;
}

export const VIDEO_DETAIL_EVENTS = new Map<
  string,
  {
    eventType:
      | 'heart_beat_event_pause'
      | 'heart_beat_event_resume'
      | 'heart_beat_event_buffer_start'
      | 'heart_beat_event_buffer_end';
    playerState: GodavariPulsePlayerState;
  }
>([
  [
    PlaybackEvent.PLAYBACK_PAUSED,
    {
      eventType: 'heart_beat_event_pause',
      playerState: 'Ready',
    },
  ],
  [
    PlaybackEvent.PLAYBACK_RESUMED,
    {
      eventType: 'heart_beat_event_resume',
      playerState: 'Playing',
    },
  ],
  [
    PlaybackEvent.PLAYBACK_BUFFERING_START,
    {
      eventType: 'heart_beat_event_buffer_start',
      playerState: 'Buffering',
    },
  ],
  [
    PlaybackEvent.PLAYBACK_BUFFERING_END,
    {
      eventType: 'heart_beat_event_buffer_end',
      playerState: 'Ready',
    },
  ],
]);

export const STARTUP_PULSE_EVENTS = new Map<string, GodavariPulseStage>([
  [
    'ManifestParsing',
    { pulse_start_steps: 'ManifestFetch', pulse_micro_steps: 'ParsingStart', pulse_player_state: 'Manifest' },
  ],
  [
    'ManifestParsed',
    { pulse_start_steps: 'ManifestFetch', pulse_micro_steps: 'ParsingEnd', pulse_player_state: 'Manifest' },
  ],
]);

export const STARTUP_PLAYER_STATES = new Map<string, GodavariPulsePlayerState>([
  [PlaybackEvent.PLAYBACK_START, 'Buffering'],
  [PlaybackEvent.PLAYBACK_PREPARED, 'Ready'],
  ['VideoAttempt', 'VideoUrl'],
]);

export const AD_DETAIL_EVENTS = new Map<string, GodavariAdDetailType>([
  [AdPlaybackEvent.AD_VIEW, 'heart_beat_ads_event_ad_click'],
  [AdPlaybackEvent.AD_PAUSED_EVENT, 'heart_beat_ads_event_ad_pause'],
  [AdPlaybackEvent.AD_RESUMED_EVENT, 'heart_beat_ads_event_ad_resume'],
  ['AdBufferStart', 'heart_beat_ads_event_ad_buffer_start'],
  ['AdBufferEnd', 'heart_beat_ads_event_ad_buffer_end'],
]);
